import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  CssBaseline,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Alert,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { axiosClientCreditCore } from "../AxiosClient";
import { buildQueryString, getFirstOfMonth, getToday } from "./siigoHelpers";
import { Btn, BtnDanger, Section } from "./siigoStyledComponents";
import TabPanel from "./TabPanel";
import BatchDateRangeFields from "./BatchDateRangeFields";
import FailedOutboxTab from "./FailedOutboxTab";
import useSiigoErrorModal from "./useSiigoErrorModal";

export default function ContabilidadSiigo() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Originación — comprobante contable (credit-core)
  const [ccOldId, setCcOldId] = useState("");
  const [ccDate, setCcDate] = useState(() => getToday());
  const [ccTransactionType, setCcTransactionType] = useState("comprobante_originacion");

  // Batch (credit-core)
  const [batchStart, setBatchStart] = useState(() => getFirstOfMonth());
  const [batchEnd, setBatchEnd] = useState(() => getToday());

  // Recaudo — comprobante contable / inverso (credit-core)
  const [quotaUuid, setQuotaUuid] = useState("");
  const [recaudoDate, setRecaudoDate] = useState(() => getToday());
  const [recaudoQuotaTxType, setRecaudoQuotaTxType] = useState("comprobante_recaudo_quota");
  const [recaudoPaymentTxType, setRecaudoPaymentTxType] = useState("comprobante_recaudo_payment");
  // Voucher directo manual: se identifica por oldId del crédito + número de cuota
  const [recaudoQuotaCreditOldId, setRecaudoQuotaCreditOldId] = useState("");
  const [recaudoQuotaNumber, setRecaudoQuotaNumber] = useState("");
  // Voucher directo automático: se identifica por oldId del PaymentRecord
  const [recaudoPaymentOldId, setRecaudoPaymentOldId] = useState("");

  // Outbox
  const [maxAttempts, setMaxAttempts] = useState(3);

  // Comisión — nota crédito (credit-core)
  const [comOldId, setComOldId] = useState("");

  const hasValidBatchRange = useMemo(
    () => Boolean(batchStart && batchEnd) && batchStart <= batchEnd,
    [batchStart, batchEnd]
  );
  const { showError, errorModal } = useSiigoErrorModal();

  const notify = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const executeRequest = useCallback(
    async (requestFn) => {
      setLoading(true);
      try {
        await requestFn();
        notify("Operacion exitosa");
      } catch (err) {
        const errorStatus = err?.response?.status;
        const errorPayload = err?.response?.data;
        const msg = err?.response?.data?.message || err?.response?.data || err.message;
        console.error("[ContabilidadSiigo] Error en operacion de Siigo", {
          status: errorStatus,
          message: msg,
          payload: errorPayload,
          error: err,
        });
        showError(msg);
      } finally {
        setLoading(false);
      }
    },
    [notify, showError]
  );

  // ── Credit-core: Originación ──────────────────────────────────────────────

  const handleInversoOriginacion = useCallback(
    () =>
      executeRequest(() =>
        axiosClientCreditCore.post(
          `/v1/siigo/voucher/credits/${ccOldId.trim()}/inverso-originacion?${buildQueryString({
            accountingDate: ccDate,
          })}`
        )
      ),
    [ccDate, ccOldId, executeRequest]
  );

  const handleCreateAndSendVoucher = useCallback(
    () =>
      executeRequest(() =>
        axiosClientCreditCore.post(
          `/v1/siigo/voucher/credits/${ccOldId.trim()}/voucher?${buildQueryString({
            transactionType: ccTransactionType,
            accountingDate: ccDate,
          })}`
        )
      ),
    [ccDate, ccOldId, ccTransactionType, executeRequest]
  );

  const handleBatchOriginacion = useCallback(
    () =>
      executeRequest(() =>
        axiosClientCreditCore.post(
          `/v1/siigo/voucher/batch-originacion?${buildQueryString({
            startDate: batchStart,
            endDate: batchEnd,
          })}`
        )
      ),
    [batchEnd, batchStart, executeRequest]
  );

  // ── Credit-core: Comisiones ───────────────────────────────────────────────

  const handleNotaCredito = useCallback(
    () =>
      executeRequest(() =>
        axiosClientCreditCore.post(
          `/v1/siigo/invoice/credits/${comOldId.trim()}/nota-credito`
        )
      ),
    [comOldId, executeRequest]
  );

  const handleBatchComisiones = useCallback(
    () =>
      executeRequest(() =>
        axiosClientCreditCore.post(
          `/v1/siigo/invoice/batch-comisiones?${buildQueryString({
            startDate: batchStart,
            endDate: batchEnd,
          })}`
        )
      ),
    [batchEnd, batchStart, executeRequest]
  );

  // ── Credit-core: Recaudo ──────────────────────────────────────────────────

  const handleVoucherRecaudoQuota = useCallback(
    () =>
      executeRequest(() =>
        axiosClientCreditCore.post(
          `/v1/siigo/recaudo/credits/${recaudoQuotaCreditOldId.trim()}/quotas/${recaudoQuotaNumber.trim()}/voucher?${buildQueryString(
            {
              transactionType: recaudoQuotaTxType,
              accountingDate: recaudoDate,
            }
          )}`
        )
      ),
    [executeRequest, recaudoQuotaCreditOldId, recaudoQuotaNumber, recaudoQuotaTxType, recaudoDate]
  );

  const handleVoucherRecaudoPayment = useCallback(
    () =>
      executeRequest(() =>
        axiosClientCreditCore.post(
          `/v1/siigo/recaudo/payments/${recaudoPaymentOldId.trim()}/voucher?${buildQueryString({
            transactionType: recaudoPaymentTxType,
            accountingDate: recaudoDate,
          })}`
        )
      ),
    [executeRequest, recaudoPaymentOldId, recaudoPaymentTxType, recaudoDate]
  );

  const handleInversoQuota = useCallback(
    () =>
      executeRequest(() =>
        axiosClientCreditCore.post(
          `/v1/siigo/recaudo/quotas/${quotaUuid.trim()}/inverso?${buildQueryString({
            accountingDate: recaudoDate,
          })}`
        )
      ),
    [executeRequest, quotaUuid, recaudoDate]
  );

  const handleInversoPayment = useCallback(
    () =>
      executeRequest(() =>
        axiosClientCreditCore.post(
          `/v1/siigo/recaudo/payments/${recaudoPaymentOldId.trim()}/inverso?${buildQueryString({
            accountingDate: recaudoDate,
          })}`
        )
      ),
    [executeRequest, recaudoPaymentOldId, recaudoDate]
  );

  const handleBatchRecaudoQuotas = useCallback(
    () =>
      executeRequest(() =>
        axiosClientCreditCore.post(
          `/v1/siigo/recaudo/batch-quotas?${buildQueryString({
            startDate: batchStart,
            endDate: batchEnd,
          })}`
        )
      ),
    [batchEnd, batchStart, executeRequest]
  );

  const handleProcessPending = useCallback(
    () =>
      executeRequest(() =>
        axiosClientCreditCore.post(
          `/v1/siigo/outbox/process-pending?maxAttempts=${maxAttempts}`
        )
      ),
    [maxAttempts, executeRequest]
  );

  const handleBatchRecaudoPayments = useCallback(
    () =>
      executeRequest(() =>
        axiosClientCreditCore.post(
          `/v1/siigo/recaudo/batch-payments?${buildQueryString({
            startDate: batchStart,
            endDate: batchEnd,
          })}`
        )
      ),
    [batchEnd, batchStart, executeRequest]
  );

  const handleBatchRecaudoPendingPayments = useCallback(
    () =>
      executeRequest(() =>
        axiosClientCreditCore.post(
          `/v1/siigo/recaudo/batch-pending-payments?${buildQueryString({
            startDate: batchStart,
            endDate: batchEnd,
          })}`
        )
      ),
    [batchEnd, batchStart, executeRequest]
  );

  return (
    <React.Fragment>
      <CssBaseline />
      <Toolbar />
      <Box
        component="main"
        sx={{
          width: "100%",
          flexGrow: 1,
          margin: "18px 14px",
          px: 1,
          pb: 3,
          pt: "80px",
          boxSizing: "border-box",
          minWidth: 0,
        }}
      >
        <Typography variant="h5" fontWeight={700} mb={2} color="#161e55">
          Contabilidad Siigo
        </Typography>

        <Paper
          elevation={3}
          sx={{
            p: 2,
            mb: 3,
            border: "2px solid #161e55",
            borderRadius: "12px",
            backgroundColor: "#f0f2ff",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} mb={1} color="#161e55">
            Procesar Outbox Pendiente
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Envía a Siigo todos los registros en estado <strong>PENDING</strong> o{" "}
            <strong>FAILED</strong> del outbox, agrupados por tipo de transacción.
          </Typography>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <TextField
              label="Max intentos por registro"
              type="number"
              value={maxAttempts}
              onChange={(e) => setMaxAttempts(Number(e.target.value))}
              size="small"
              sx={{ width: 200 }}
              inputProps={{ min: 1, max: 10 }}
            />
            <Btn
              onClick={handleProcessPending}
              disabled={loading}
              sx={{
                backgroundColor: "#1565c0",
                "&:hover": { backgroundColor: "#42a5f5" },
                px: 3,
                fontWeight: 700,
              }}
            >
              {loading ? <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} /> : null}
              Enviar todos los pendientes
            </Btn>
          </Box>
        </Paper>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="inherit"
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ style: { backgroundColor: "#161e55" } }}
          sx={{ mb: 1 }}
        >
          <Tab label="Originación" />
          <Tab label="Comisiones" />
          <Tab label="Recaudo" />
          <Tab label="Comprobantes con error" />
        </Tabs>

        <Divider sx={{ mb: 2 }} />

        {/* ── TAB 0: ORIGINACIÓN ─────────────────────────────────────────── */}
        <TabPanel value={tab} index={0}>
          <Section elevation={2}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Comprobante contable — credit-core
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  label="ID Crédito (oldId)"
                  value={ccOldId}
                  onChange={(e) => setCcOldId(e.target.value)}
                  size="small"
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Fecha contable"
                  type="date"
                  value={ccDate}
                  onChange={(e) => setCcDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="transactionType (voucher directo)"
                  value={ccTransactionType}
                  onChange={(e) => setCcTransactionType(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              <Btn
                onClick={handleCreateAndSendVoucher}
                disabled={!ccOldId || loading}
                sx={{ backgroundColor: "#37474f", "&:hover": { backgroundColor: "#78909c" } }}
              >
                Correr comprobante contable
              </Btn>
              <BtnDanger onClick={handleInversoOriginacion} disabled={!ccOldId || loading}>
                Inverso originación
              </BtnDanger>
            </Box>
          </Section>

          <Section elevation={2}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Batch — credit-core
            </Typography>
            <BatchDateRangeFields
              batchStart={batchStart}
              batchEnd={batchEnd}
              setBatchStart={setBatchStart}
              setBatchEnd={setBatchEnd}
            />
            <Box mt={2} display="flex" gap={1}>
              <Btn onClick={handleBatchOriginacion} disabled={loading || !hasValidBatchRange}>
                Batch originación
              </Btn>
            </Box>
          </Section>

        </TabPanel>

        {/* ── TAB 1: COMISIONES ──────────────────────────────────────────── */}
        <TabPanel value={tab} index={1}>
          <Section elevation={2}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Nota crédito — credit-core
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  label="ID Crédito (oldId)"
                  value={comOldId}
                  onChange={(e) => setComOldId(e.target.value)}
                  size="small"
                  fullWidth
                  type="number"
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              <BtnDanger onClick={handleNotaCredito} disabled={!comOldId || loading}>
                Nota crédito (anular factura)
              </BtnDanger>
            </Box>
          </Section>

          <Section elevation={2}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Batch — credit-core
            </Typography>
            <BatchDateRangeFields
              batchStart={batchStart}
              batchEnd={batchEnd}
              setBatchStart={setBatchStart}
              setBatchEnd={setBatchEnd}
            />
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              <Btn onClick={handleBatchComisiones} disabled={loading || !hasValidBatchRange}>
                Batch comisiones
              </Btn>
            </Box>
          </Section>

        </TabPanel>

        {/* ── TAB 2: RECAUDO ─────────────────────────────────────────────── */}
        <TabPanel value={tab} index={2}>
          <Section elevation={2}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Por Cuota (manual) — credit-core
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField
                  label="quotaUuid (inverso)"
                  value={quotaUuid}
                  onChange={(e) => setQuotaUuid(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fecha contable"
                  type="date"
                  value={recaudoDate}
                  onChange={(e) => setRecaudoDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              <BtnDanger onClick={handleInversoQuota} disabled={!quotaUuid || loading}>
                Inverso cuota
              </BtnDanger>
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" fontWeight={600} mb={1} color="#161e55">
              Generar comprobante directo — por ID crédito (oldId) + número de cuota
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  label="ID Crédito (oldId)"
                  type="number"
                  value={recaudoQuotaCreditOldId}
                  onChange={(e) => setRecaudoQuotaCreditOldId(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="# Cuota (quotaNumber)"
                  type="number"
                  value={recaudoQuotaNumber}
                  onChange={(e) => setRecaudoQuotaNumber(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="transactionType"
                  value={recaudoQuotaTxType}
                  onChange={(e) => setRecaudoQuotaTxType(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              <Btn
                onClick={handleVoucherRecaudoQuota}
                disabled={!recaudoQuotaCreditOldId || !recaudoQuotaNumber || loading}
                sx={{ backgroundColor: "#37474f", "&:hover": { backgroundColor: "#78909c" } }}
              >
                Enviar voucher directo
              </Btn>
            </Box>
          </Section>

          <Section elevation={2}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Por PaymentRecord (automático) — credit-core
            </Typography>
            <Typography variant="body2" fontWeight={600} mb={1} color="#161e55">
              Comprobante directo / inverso — por ID PaymentRecord (oldId)
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  label="ID PaymentRecord (oldId)"
                  type="number"
                  value={recaudoPaymentOldId}
                  onChange={(e) => setRecaudoPaymentOldId(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="transactionType (voucher directo)"
                  value={recaudoPaymentTxType}
                  onChange={(e) => setRecaudoPaymentTxType(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Fecha contable"
                  type="date"
                  value={recaudoDate}
                  onChange={(e) => setRecaudoDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              <Btn
                onClick={handleVoucherRecaudoPayment}
                disabled={!recaudoPaymentOldId || loading}
                sx={{ backgroundColor: "#37474f", "&:hover": { backgroundColor: "#78909c" } }}
              >
                Enviar voucher directo
              </Btn>
              <BtnDanger onClick={handleInversoPayment} disabled={!recaudoPaymentOldId || loading}>
                Inverso payment
              </BtnDanger>
            </Box>
          </Section>

          <Section elevation={2}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Batch — credit-core
            </Typography>
            <BatchDateRangeFields
              batchStart={batchStart}
              batchEnd={batchEnd}
              setBatchStart={setBatchStart}
              setBatchEnd={setBatchEnd}
            />
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              <Btn onClick={handleBatchRecaudoQuotas} disabled={loading || !hasValidBatchRange}>
                Batch cuotas
              </Btn>
              <Btn onClick={handleBatchRecaudoPayments} disabled={loading || !hasValidBatchRange}>
                Batch payment records
              </Btn>
              <Btn onClick={handleBatchRecaudoPendingPayments} disabled={loading || !hasValidBatchRange}>
                Batch PENDING_APPLICATION
              </Btn>
            </Box>
          </Section>

        </TabPanel>

        {/* ── TAB 3: COMPROBANTES CON ERROR ─────────────────────────────── */}
        <TabPanel value={tab} index={3}>
          <FailedOutboxTab notify={notify} />
        </TabPanel>

      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      {errorModal}
    </React.Fragment>
  );
}
